const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeNumber = (value) => {
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const roundMetric = (value) => {
  const numericValue = normalizeNumber(value);

  return numericValue === null ? null : Number(numericValue.toFixed(6));
};

export const PRODUCT_CALCULATION_MODES = {
  fixed: 'fixed',
  areaWh: 'area_wh',
  areaWl: 'area_wl',
  volumeWhl: 'volume_whl',
};

export const PRODUCT_CALCULATION_MODE_OPTIONS = [
  { value: PRODUCT_CALCULATION_MODES.fixed, label: 'Fixed' },
  { value: PRODUCT_CALCULATION_MODES.areaWh, label: 'Area (Width x Height)' },
  { value: PRODUCT_CALCULATION_MODES.areaWl, label: 'Area (Width x Length)' },
  { value: PRODUCT_CALCULATION_MODES.volumeWhl, label: 'Volume (Width x Height x Length)' },
];

const PRODUCT_CALCULATION_MODE_VALUES = PRODUCT_CALCULATION_MODE_OPTIONS.map(
  (option) => option.value
);

const DIMENSION_FIELDS = ['width', 'height', 'length'];

export const normalizeProductCalculationMode = (value) =>
  PRODUCT_CALCULATION_MODE_VALUES.includes(value)
    ? value
    : PRODUCT_CALCULATION_MODES.fixed;

export const getCalculationModeRequiredInputs = (calculationMode) => {
  const normalizedMode = normalizeProductCalculationMode(calculationMode);

  if (normalizedMode === PRODUCT_CALCULATION_MODES.areaWh) {
    return ['width', 'height'];
  }

  if (normalizedMode === PRODUCT_CALCULATION_MODES.areaWl) {
    return ['width', 'length'];
  }

  if (normalizedMode === PRODUCT_CALCULATION_MODES.volumeWhl) {
    return ['width', 'height', 'length'];
  }

  return [];
};

export const normalizeDimensionFields = (fields = []) => {
  const rawFields = Array.isArray(fields) ? fields : [];

  return Array.from(
    new Set(
      rawFields
        .map((field) => normalizeString(field).toLowerCase())
        .filter((field) => DIMENSION_FIELDS.includes(field))
    )
  );
};

const inferDimensionFields = (dimensions = {}) => {
  const storedFields = normalizeDimensionFields(
    dimensions?.fields || dimensions?.requiredInputs
  );

  if (storedFields.length > 0) {
    return storedFields;
  }

  return DIMENSION_FIELDS.filter((field) => roundMetric(dimensions?.[field]) !== null);
};

export const getConfigurationDimensionFields = ({
  dimensions = {},
  requiredInputs = [],
  calculationMode = '',
} = {}) => {
  const requiredDimensionFields = normalizeDimensionFields(requiredInputs);

  if (requiredDimensionFields.length > 0) {
    return requiredDimensionFields;
  }

  const modeFields = getCalculationModeRequiredInputs(calculationMode);

  if (modeFields.length > 0) {
    return modeFields;
  }

  return inferDimensionFields(dimensions);
};

export const normalizeConfigurationDimensions = (
  dimensions = {},
  {
    calculationMode = dimensions?.calculationMode,
    requiredInputs = dimensions?.fields || dimensions?.requiredInputs,
  } = {}
) => {
  const normalizedCalculationMode = normalizeProductCalculationMode(
    calculationMode
  );
  const fields = getConfigurationDimensionFields({
    dimensions,
    requiredInputs,
    calculationMode: normalizedCalculationMode,
  });
  const width = roundMetric(dimensions?.width);
  const height = roundMetric(dimensions?.height);
  const length = roundMetric(dimensions?.length);
  const area = normalizedCalculationMode === PRODUCT_CALCULATION_MODES.areaWh &&
    width !== null &&
    height !== null
    ? roundMetric(width * height)
    : normalizedCalculationMode === PRODUCT_CALCULATION_MODES.areaWl &&
      width !== null &&
      length !== null
      ? roundMetric(width * length)
      : dimensions?.area !== undefined &&
        dimensions?.volume === undefined &&
        normalizedCalculationMode === PRODUCT_CALCULATION_MODES.fixed
        ? roundMetric(dimensions.area)
        : null;
  const volume = normalizedCalculationMode === PRODUCT_CALCULATION_MODES.volumeWhl &&
    width !== null &&
    height !== null &&
    length !== null
    ? roundMetric(width * height * length)
    : dimensions?.volume !== undefined &&
      normalizedCalculationMode === PRODUCT_CALCULATION_MODES.fixed
      ? roundMetric(dimensions.volume)
      : null;

  return {
    width: fields.includes('width') || width !== null ? width : null,
    height: fields.includes('height') || height !== null ? height : null,
    length: fields.includes('length') || length !== null ? length : null,
    area,
    volume,
    calculationMode: normalizedCalculationMode,
    fields,
    enabled: fields.length > 0 || width !== null || height !== null || length !== null,
  };
};

export const validateConfigurationDimensions = (dimensions = {}, options = {}) => {
  const normalizedDimensions = normalizeConfigurationDimensions(
    dimensions,
    options
  );
  const requiredFields = getConfigurationDimensionFields({
    dimensions: normalizedDimensions,
    requiredInputs: options.requiredInputs || normalizedDimensions.fields,
    calculationMode: normalizedDimensions.calculationMode,
  });

  if (requiredFields.length === 0) {
    return normalizedDimensions;
  }

  const missingField = requiredFields.find(
    (field) => normalizedDimensions[field] === null
  );

  if (missingField) {
    throw new Error(
      `${missingField.charAt(0).toUpperCase() + missingField.slice(1)} is required.`
    );
  }

  const invalidField = requiredFields.find(
    (field) => normalizedDimensions[field] <= 0
  );

  if (invalidField) {
    throw new Error('Dimensions must be positive numbers.');
  }

  return normalizedDimensions;
};

export const getConfigurationDimensionKey = (dimensions = {}, options = {}) => {
  const normalizedDimensions = normalizeConfigurationDimensions(
    dimensions,
    options
  );
  const fields = getConfigurationDimensionFields({
    dimensions: normalizedDimensions,
    requiredInputs: options.requiredInputs || normalizedDimensions.fields,
    calculationMode: normalizedDimensions.calculationMode,
  });

  if (fields.length === 0) {
    return '';
  }

  return fields.map((field) => normalizedDimensions[field] ?? '').join('x');
};

const formatDimensionPart = (value) => {
  const numericValue = normalizeNumber(value);

  if (numericValue === null) {
    return '';
  }

  return Number.isInteger(numericValue)
    ? String(numericValue)
    : String(Number(numericValue.toFixed(3)));
};

export const suggestConfigurationName = (
  productName = '',
  dimensions = {},
  options = {}
) => {
  const normalizedName = normalizeString(productName);
  const normalizedDimensions = normalizeConfigurationDimensions(
    dimensions,
    options
  );
  const fields = getConfigurationDimensionFields({
    dimensions: normalizedDimensions,
    requiredInputs: options.requiredInputs || normalizedDimensions.fields,
    calculationMode: normalizedDimensions.calculationMode,
  });

  if (!normalizedName || fields.length === 0) {
    return normalizedName;
  }

  const parts = fields
    .map((field) => formatDimensionPart(normalizedDimensions[field]))
    .filter(Boolean);

  if (parts.length !== fields.length) {
    return normalizedName;
  }

  return `${normalizedName} ${parts.join('x')}`;
};

export const findConfigurationByDimensions = (
  configurations = [],
  dimensions = {},
  ignoredConfigurationId = '',
  options = {}
) => {
  const dimensionKey = getConfigurationDimensionKey(dimensions, options);

  if (!dimensionKey) {
    return null;
  }

  return (Array.isArray(configurations) ? configurations : []).find(
    (configuration) =>
      configuration?.id !== ignoredConfigurationId &&
      getConfigurationDimensionKey(configuration?.dimensions, options) === dimensionKey
  ) || null;
};

const SUPPORTED_PRICING_METHODS = ['perUnit', 'perSquareMeter'];

const LEGACY_PRICING_METHOD_MAP = {
  fixedPrice: 'perUnit',
  perMeter: 'perMeter',
  perCubicMeter: 'perCubicMeter',
  perDay: 'perDay',
  perPerson: 'perPerson',
};

const normalizePricingMethod = (value) => {
  const normalizedValue = normalizeString(value);

  if (SUPPORTED_PRICING_METHODS.includes(normalizedValue)) {
    return normalizedValue;
  }

  return LEGACY_PRICING_METHOD_MAP[normalizedValue] || 'perUnit';
};

const getPricingQuantity = (component = {}, componentSupplier = {}, dimensions = {}) => {
  const pricingMethod = normalizePricingMethod(componentSupplier?.pricingMethod);
  const normalizedDimensions = normalizeConfigurationDimensions(dimensions);

  if (pricingMethod === 'perSquareMeter') {
    return normalizedDimensions.area;
  }

  if (pricingMethod === 'perUnit') {
    return normalizeNumber(component?.quantity) ?? 1;
  }

  return null;
};

export const calculateComponentLineTotalFromPrice = (
  component = {},
  componentSupplier = {},
  dimensions = {}
) => {
  const selectedUnitPrice = getComponentSupplierPrice(componentSupplier);

  if (selectedUnitPrice === null) {
    return null;
  }

  const quantity = getPricingQuantity(component, componentSupplier, dimensions);

  if (quantity === null) {
    return null;
  }

  return quantity * selectedUnitPrice;
};

export const getComponentCalculationBreakdown = (
  component = {},
  componentSupplier = {},
  dimensions = {}
) => {
  const selectedUnitPrice = getComponentSupplierPrice(componentSupplier);
  const pricingMethod = normalizePricingMethod(componentSupplier?.pricingMethod);
  const quantity = getPricingQuantity(component, componentSupplier, dimensions);
  const lineTotal = calculateComponentLineTotalFromPrice(
    component,
    componentSupplier,
    dimensions
  );

  return {
    pricingMethod,
    quantity,
    rate: selectedUnitPrice,
    lineTotal,
  };
};

export const hasValidComponentSupplierPrice = (componentSupplier) => {
  const price = normalizeNumber(componentSupplier?.currentPrice);

  return price !== null && price > 0;
};

export const getComponentSupplierPrice = (componentSupplier) =>
  hasValidComponentSupplierPrice(componentSupplier)
    ? Number(componentSupplier.currentPrice)
    : null;

export const getActiveComponentSuppliers = (componentSuppliers = []) =>
  (Array.isArray(componentSuppliers) ? componentSuppliers : []).filter(
    (componentSupplier) => componentSupplier?.isActive === true
  );

export const getSelectedComponentSupplier = (
  component = {},
  componentSuppliers = []
) => {
  const selectedComponentSupplierId = normalizeString(
    component?.selectedComponentSupplierId
  );
  const selectedSupplierId = normalizeString(component?.selectedSupplierId);
  const activeSuppliers = getActiveComponentSuppliers(componentSuppliers);
  const selectedById = activeSuppliers.find(
    (componentSupplier) =>
      selectedComponentSupplierId &&
      componentSupplier?.id === selectedComponentSupplierId
  );

  if (selectedById) {
    return selectedById;
  }

  return activeSuppliers.find(
    (componentSupplier) =>
      selectedSupplierId &&
      componentSupplier?.supplierId === selectedSupplierId
  ) || null;
};

export const calculateComponentStatus = (
  component = {},
  componentSuppliers = []
) => {
  if (component?.isDeleted === true) {
    return 'deleted';
  }

  const activeSuppliers = getActiveComponentSuppliers(componentSuppliers);

  if (activeSuppliers.length === 0) {
    return 'needs_supplier';
  }

  if (!activeSuppliers.some(hasValidComponentSupplierPrice)) {
    return 'needs_price';
  }

  const selectedSupplier = getSelectedComponentSupplier(component, activeSuppliers);

  if (!selectedSupplier) {
    return component?.isRequired === true
      ? 'needs_selection'
      : 'optional_unselected';
  }

  return 'ready';
};

export const calculateComponentLineTotal = (
  component = {},
  componentSuppliers = [],
  dimensions = {}
) => {
  if (component?.isDeleted === true) {
    return null;
  }

  const selectedSupplier = getSelectedComponentSupplier(
    component,
    componentSuppliers
  );
  if (!selectedSupplier) {
    return null;
  }

  return calculateComponentLineTotalFromPrice(
    component,
    selectedSupplier,
    dimensions
  );
};

export const calculateComponentPricing = (
  component = {},
  componentSuppliers = [],
  dimensions = {}
) => {
  const selectedSupplier = getSelectedComponentSupplier(
    component,
    componentSuppliers
  );
  const selectedUnitPrice = getComponentSupplierPrice(selectedSupplier);
  const lineTotal = calculateComponentLineTotal(
    component,
    componentSuppliers,
    dimensions
  );

  return {
    selectedComponentSupplierId: selectedSupplier?.id || '',
    selectedSupplierId: selectedSupplier?.supplierId || '',
    selectedUnitPrice,
    lineTotal,
    calculationBreakdown: selectedSupplier
      ? getComponentCalculationBreakdown(component, selectedSupplier, dimensions)
      : null,
    status: calculateComponentStatus(component, componentSuppliers),
    selectedSupplier,
  };
};

export const validateComponentSupplierSelection = (
  component = {},
  componentSuppliers = [],
  selectedComponentSupplierId = ''
) => {
  const normalizedSelectionId = normalizeString(selectedComponentSupplierId);

  if (!normalizedSelectionId) {
    throw new Error('Component supplier selection is required.');
  }

  const selectedSupplier = getActiveComponentSuppliers(componentSuppliers).find(
    (componentSupplier) => componentSupplier?.id === normalizedSelectionId
  );

  if (!selectedSupplier) {
    throw new Error('Selected supplier must belong to this component.');
  }

  if (selectedSupplier.componentId !== component?.id) {
    throw new Error('Selected supplier must belong to this component.');
  }

  if (!hasValidComponentSupplierPrice(selectedSupplier)) {
    throw new Error('Selected supplier must have a valid current price.');
  }

  return calculateComponentPricing(
    {
      ...component,
      selectedComponentSupplierId: selectedSupplier.id,
      selectedSupplierId: selectedSupplier.supplierId,
    },
    componentSuppliers
  );
};

export const calculateMixedProductComponents = (
  components = [],
  componentSuppliersByComponentId = {},
  dimensions = {}
) =>
  (Array.isArray(components) ? components : [])
    .filter((component) => component?.isDeleted !== true)
    .map((component) => ({
      ...component,
      ...calculateComponentPricing(
        component,
        componentSuppliersByComponentId?.[component.id] || [],
        dimensions
      ),
    }));

export const calculateMixedProductTotal = (
  components = [],
  componentSuppliersByComponentId = {},
  dimensions = {}
) =>
  calculateMixedProductComponents(
    components,
    componentSuppliersByComponentId,
    dimensions
  ).reduce((total, component) => {
    const lineTotal = normalizeNumber(component.lineTotal);

    return lineTotal === null ? total : total + lineTotal;
  }, 0);
